/**
 * Attach a Uspacy portal to this organization.
 *
 * The embedded shell cannot do this itself: it only proves WHICH portal is
 * asking, and the decision needs an authenticated Aelo session — which
 * organization, current versioned legal actions, and an authorized documented
 * instruction to process that portal's conversations (plan §1.2, §16.1). So
 * the shell mints a one-time claim, opens this page with it, and the operator
 * finishes here.
 *
 * A matching email is deliberately NOT a link: the claim plus an authenticated
 * session is the whole authority, and nothing about the portal grants access to
 * an existing Aelo organization.
 */
import { useCallback, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { UspacyLinkPageShell } from '@/components/settings/UspacyLinkPageShell';
import {
  EMPTY_USPACY_LEGAL_ACTIONS,
  UspacyLegalActions,
} from '@/components/legal/UspacyLegalActions';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import {
  buildUspacyLegalDocuments,
  normalizeUspacyPlacementLocale,
} from '@aica/api-types';

interface UspacyLinkSearch {
  portal?: string;
  claim?: string;
  lang?: string;
}

export const Route = createFileRoute('/integrations/uspacy/link')({
  validateSearch: (search: Record<string, unknown>): UspacyLinkSearch => ({
    portal: typeof search.portal === 'string' ? search.portal : undefined,
    claim: typeof search.claim === 'string' ? search.claim : undefined,
    lang: typeof search.lang === 'string' ? search.lang : undefined,
  }),
  component: UspacyLinkPage,
});

type Phase = 'idle' | 'linking' | 'linked';

function UspacyLinkPage() {
  const { t } = useTranslation('settings');
  const { portal, claim, lang } = Route.useSearch();
  const { orgId } = useAuth();
  const { authenticatedFetch } = useAuthFetch();

  const [legalActions, setLegalActions] = useState({
    ...EMPTY_USPACY_LEGAL_ACTIONS,
  });
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const locale = normalizeUspacyPlacementLocale(lang);
  const legalDocuments = buildUspacyLegalDocuments('https://aelo.cloud', locale);
  const allConfirmed = Object.values(legalActions).every(Boolean);

  const link = useCallback(async () => {
    if (!orgId || !portal || !claim) return;
    setPhase('linking');
    setError(null);
    try {
      await authenticatedFetch(`/api/v1/organizations/${orgId}/uspacy/link`, {
        method: 'POST',
        body: JSON.stringify({
          portalDomain: portal,
          claimToken: claim,
          legal: {
            terms: {
              documentId: legalDocuments.terms.documentId,
              version: legalDocuments.terms.version,
              accepted: true,
            },
            privacy: {
              documentId: legalDocuments.privacy.documentId,
              version: legalDocuments.privacy.version,
              acknowledged: true,
            },
            processingInstruction: {
              documentId: legalDocuments.processingInstruction.documentId,
              version: legalDocuments.processingInstruction.version,
              confirmed: true,
              authorityConfirmed: true,
            },
            language: locale,
          },
        }),
      });
      setPhase('linked');
    } catch (cause) {
      setPhase('idle');
      setError(cause instanceof Error ? cause.message : t('uspacyLink.genericError'));
    }
  }, [authenticatedFetch, claim, legalDocuments, locale, orgId, portal, t]);

  // A claim is single-use and short-lived. Landing here without one means the
  // link was opened twice, or long after the placement issued it.
  if (!portal || !claim) {
    return (
      <UspacyLinkPageShell title={t('uspacyLink.title')}>
        <p className="text-sm text-fg-muted">{t('uspacyLink.missingClaim')}</p>
      </UspacyLinkPageShell>
    );
  }

  if (phase === 'linked') {
    return (
      <UspacyLinkPageShell title={t('uspacyLink.title')}>
        <p className="text-sm text-fg">{t('uspacyLink.done', { portal })}</p>
        <p className="text-sm text-fg-muted">{t('uspacyLink.doneHint')}</p>
      </UspacyLinkPageShell>
    );
  }

  return (
    <UspacyLinkPageShell title={t('uspacyLink.title')}>
      <p className="text-sm text-fg-muted">{t('uspacyLink.intro', { portal })}</p>

      <UspacyLegalActions
        documents={legalDocuments}
        actions={legalActions}
        onActionChange={(action, checked) =>
          setLegalActions((current) => ({ ...current, [action]: checked }))
        }
      />

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      <Button
        onClick={() => void link()}
        disabled={!allConfirmed || phase === 'linking' || !orgId}
      >
        {phase === 'linking' ? t('uspacyLink.linking') : t('uspacyLink.submit')}
      </Button>
    </UspacyLinkPageShell>
  );
}
